import { useTranslation } from "react-i18next";
import BackButton from "../../features/path/backButton";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { BreadCrumbsEnum } from "../../models/enum/BreadCrumbsEnum";
import { Button, Card, DatePicker, Modal, ProgressIndicator, Select, Table, TableColumnWidth, TableColumns, TableData, TablePreferences, TextArea, TextBox } from "@veneer/core";
import { Grid } from "@mui/material";
import feedBackService from "../../services/agendaApi/feedBack.service";
import appointmentService from "../../services/agendaApi/appointment.service";
import { IAppointmentDto } from "../../models/dto/agendaApi/IAppointmentDto";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { IFeedBackDto } from "../../models/dto/agendaApi/IFeedBackDto";
import { dateToString } from "../../utils/conversion";
import '../../assets/css/views/activities/feedBack.css';
import activitiesService from "../../services/agendaApi/activities.service";

export default function FeedBack() {
    const [t] = useTranslation("global");
    const params = useParams();
    const [appointmentActivityId, setAppointmentActivityId] = useState(0);
    const [activityId, setActivityId] = useState(0);
    const [date, setDate] = useState('');
    const [sendingData, setSendingData] = useState(false);
    const [feedBacks, setFeedBacks] = useState<IFeedBackDto[]>([]);
    const [posCode, setPosCode] = useState('');
    const languageId: any = useSelector((state: RootState) => state.languageId);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [feedBackSelected, setFeedBackSelected] = useState<IFeedBackDto>({ appointmentActivityID: appointmentActivityId, activity: '', activityID: 0, comments: '', photo: '', title: '' });
    const [openModal, setOpenModal] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [options, setOptions] = useState<any>([]);
    const [value, setValue] = useState<any>([]);
    const [data, setData] = useState<TableData[]>([]);

    useEffect(() => {
        setAppointmentActivityId(Number(params.id));
        setActivityId(Number(params.activityId));
        getPosCode();
        setCurrentDate();
    }, []);

    useEffect(() => {
        if (activityId !== 0)
            getPosCode();
    }, [activityId]);

    useEffect(() => {
        if (posCode !== '') {
            getActivitiesByAppointment();
            getFeedBacks();
        }
    }, [posCode])

    useEffect(() => {
        if (dataLoaded) {
            getFeedBacks();
        }
    }, [date, languageId]);

    const getActivitiesByAppointment = () => {
        activitiesService.getActivitiesByAppointment(activityId, languageId.value)
            .then(activities => {
                const items: any = [];
                activities.map((activity: any) => {
                    items.push({ value: activity.activityID, label: activity.activityName })
                });
                setOptions(items);
            })
    }

    const getPosCode = () => {
        appointmentService.getAppointmentByUser()
            .then((r: IAppointmentDto[]) => {
                const posCode = r.find(x => x.appointmentId == activityId)?.posCode;
                if (posCode) {
                    setPosCode(posCode);
                }
            })
    }

    const getFeedBacks = () => {
        setDataLoaded(false);
        feedBackService.getFeedBacks(posCode, date, languageId.value)
            .then((r: IFeedBackDto[]) => {
                setFeedBacks(r);
                setDataLoaded(true);
                fillData(r);
            })
    }

    const setCurrentDate = () => {
        const today = new Date();
        const dateString = dateToString(today);
        setDate(dateString);
    }

    const handleButtonDetails = (feedBack: IFeedBackDto) => {
        setFeedBackSelected(feedBack);
        setOpenModal(true);
        setReadOnly(true);
    }

    const handleButtonCreate = () => {
        setOpenModal(true);
        setReadOnly(false);
    }

    const onChangeDate = (newDate: any) => {
        const dateString = dateToString(newDate._d);
        setDate(dateString);
    };

    const onChangeTitle = (event: any) => {
        setFeedBackSelected({
            ...feedBackSelected,
            title: event
        });
    };

    const onChangeComments = (event: any) => {
        setFeedBackSelected({
            ...feedBackSelected,
            comments: event,
            appointmentActivityID: appointmentActivityId
        });
    };

    const onChange = (selectedOption: any) => {
        setValue([selectedOption.value]);
        setFeedBackSelected({
            ...feedBackSelected,
            activityID: selectedOption.value,
            activity: selectedOption.label
        });
    }

    const onClear = () => {
        setValue([]);
        setFeedBackSelected({
            ...feedBackSelected,
            activityID: 0,
            activity: ''
        });
    }

    const resetFeedBack = () => {
        setFeedBackSelected({
            ...feedBackSelected,
            title: '',
            activity: '',
            activityID: 0,
            comments: '',
            photo: ''
        });
        setValue([]);
    }

    const onCloseModal = () => {
        resetFeedBack();
        setOpenModal(false);
    }

    const insertFeedBack = () => {
        setSendingData(true);
        feedBackService.insertFeedBack(feedBackSelected)
            .then(r => {
                if (r === 1) {
                    setOpenModal(false);
                    getFeedBacks();
                    resetFeedBack();
                }
                setSendingData(false);
            });
    }

    const fillData = (data: IFeedBackDto[]) => {
        const pagedData: TableData[] = [];
        for (let i = 0; i < data.length; i++) {
            const dataFeedBack: IFeedBackDto = data[i];
            let tableData: TableData = {};
            tableData = {
                'title': dataFeedBack.title,
                'activity': dataFeedBack.activity,
                'description': <div style={{ whiteSpace: 'pre-wrap' }} >{dataFeedBack.comments}</div>
            }
            pagedData.push(tableData);
        }
        setData(pagedData);
    }

    const columns: TableColumns[] = [
        { id: 'title', label: t('header.title') },
        { id: 'activity', label: t('header.activity') },
        { id: 'description', label: t('header.description') }
    ];

    const columnsWidth: TableColumnWidth[] = [
        { columnId: 'title', width: '25%', minWidth: '25%', maxWidth: '25%' },
        { columnId: 'activity', width: '25%', minWidth: '25%', maxWidth: '25%' },
        { columnId: 'description', width: '50%', minWidth: '50%', maxWidth: '50%' },
    ]

    const tablePreferences: TablePreferences = {
        width: columnsWidth
    }


    return (
        <div>
            <div style={{ padding: '20px' }}>
                <BackButton route={BreadCrumbsEnum.Feedback} activityId={activityId} appointmentActivityId={appointmentActivityId} />
                <h4>{t("header.feedback")}</h4>
                <div style={{ padding: '20px' }}>
                    {
                        <div>
                            <Grid container spacing={2} style={{ marginBottom: '2%' }}>
                                <Grid item xs={6} style={{ textAlign: 'left' }}>
                                    <DatePicker
                                        calendarOrientation={['right', 'down']}
                                        label={t("header.date")}
                                        onChange={onChangeDate}
                                        orientation="horizontal"
                                        placeholder="mm/dd/yyyy"
                                        value={date}
                                    />
                                </Grid>
                                <Grid item xs={6} style={{ textAlign: 'right' }}>
                                    {
                                        <Button onClick={() => handleButtonCreate()} >
                                            {t("header.reportNovelty")}
                                        </Button>
                                    }
                                </Grid>
                            </Grid>
                            <div>
                                <Card
                                    appearance="dropShadow"
                                    style={{ padding: '2%', height: '100%' }}
                                    content={
                                        <div>

                                            {
                                                !dataLoaded
                                                    ?
                                                    <div className="centerFeedBack">
                                                        <ProgressIndicator
                                                            appearance="circular"
                                                            behavior="indeterminate"
                                                        />
                                                    </div>
                                                    :
                                                    feedBacks.length === 0
                                                        ?
                                                        <div style={{ textAlign: 'center', marginTop: '5%', marginBottom: '5%' }}>
                                                            {t('header.noFeedbackAlert')}
                                                        </div>
                                                        :
                                                        <div>
                                                            <Table
                                                                preferences={tablePreferences}
                                                                loading={!dataLoaded}
                                                                columns={columns}
                                                                data={data}
                                                                css={{
                                                                    height: '60vh'
                                                                }}
                                                                behavior={{
                                                                    type: 'singlePage',
                                                                }}
                                                                columnResize={true}
                                                            />
                                                        </div>
                                            }
                                        </div>
                                    }
                                />
                            </div>
                        </div>
                    }
                </div>
                <Modal
                    align="start"
                    id="modal-id"
                    closeButton
                    show={openModal}
                    onClose={() => onCloseModal()}
                    footer={(
                        <div style={{ textAlign: 'center' }}>
                            {
                                !readOnly &&
                                <Button disabled={feedBackSelected.title === '' || feedBackSelected.activityID === 0 || feedBackSelected.comments === '' || sendingData} style={{ marginRight: 4 }} onClick={() => insertFeedBack()}>{t('header.save')}</Button>
                            }
                        </div>
                    )}
                    title={t("header.reportNovelty")}>
                    <TextBox
                        id="textBoxTitle"
                        defaultValue={feedBackSelected.title}
                        label={t('header.title')}
                        placeholder={t('header.title')}
                        readOnly={readOnly}
                        required
                        value={feedBackSelected.title}
                        onChange={onChangeTitle}
                    />
                    <br />
                    <Select
                        readOnly={readOnly}
                        required
                        options={options}
                        id="select-usage"
                        label={t('header.activity')}
                        placeholder={t('header.activity')}
                        placement="bottom"
                        helperTextVisibility="auto"
                        onChange={onChange}
                        onClear={onClear}
                        value={value}
                    />
                    <br />
                    <TextArea
                        id="textAreaDetails"
                        defaultValue={feedBackSelected.comments}
                        label={t('header.details')}
                        placeholder={t('header.description')}
                        readOnly={readOnly}
                        required
                        value={feedBackSelected.comments}
                        onChange={onChangeComments}
                    />
                    <br />
                </Modal>
            </div>
        </div>
    )
}